import { useCallback, useState } from 'react';

/** @typedef {'asc' | 'desc'} SortDirection */

/**
 * @param {string | null} [defaultKey]
 * @param {SortDirection} [defaultDir]
 */
export function useTableSort(defaultKey = null, defaultDir = 'asc') {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const onSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
  }, []);

  return { sortKey, sortDir, onSort, setSortKey, setSortDir };
}

/**
 * @template T
 * @param {T[]} rows
 * @param {string | null} sortKey
 * @param {SortDirection} sortDir
 * @param {Record<string, (row: T) => unknown>} accessors
 * @returns {T[]}
 */
export function sortRows(rows, sortKey, sortDir, accessors) {
  if (!sortKey || !accessors[sortKey]) return rows;
  const getVal = accessors[sortKey];
  const dir = sortDir === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const av = getVal(a);
    const bv = getVal(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    if (typeof av === 'number' && typeof bv === 'number') {
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    }

    const as = String(av).toLowerCase();
    const bs = String(bv).toLowerCase();
    if (as === bs) return 0;
    return as > bs ? dir : -dir;
  });
}
